import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { IFilterDefinition, OptionType } from '@evinced-private/ui-common';

import { IFilterOptions } from 'src/common/helpers/DataFilterHelper';
import {
	queryParamsToFilters,
	updateUrlWithFilters
} from 'src/common/helpers/FilterQueryParamsHelper';
import { Logger } from 'src/common/services/Logger';

interface UseFiltersWithQueryParamsParams {
	filterDefinitions: IFilterDefinition[];
	filterOptions: Record<string, OptionType[]>;
}

interface UseFiltersWithQueryParamsReturn {
	filters: IFilterOptions;
	handleFilterChange: (newFilters: IFilterOptions) => void;
}

const getReadyOptionKeys = (
	filterOptions: Record<string, OptionType[]>,
	search: string
): string => {
	const urlKeys = new Set(new URLSearchParams(search).keys());
	return Object.keys(filterOptions)
		.filter((key) => urlKeys.has(key) && filterOptions[key]?.length > 0)
		.sort()
		.join(',');
};

export const useFiltersWithQueryParams = ({
	filterDefinitions,
	filterOptions
}: UseFiltersWithQueryParamsParams): UseFiltersWithQueryParamsReturn => {
	const location = useLocation();
	const history = useHistory();

	const [filters, setFilters] = useState<IFilterOptions>(() => {
		const hasOptions = Object.keys(filterOptions).length > 0;
		const hasDefinitions = filterDefinitions.length > 0;
		const hasSearch = Boolean(location.search);

		if (hasDefinitions && hasOptions && hasSearch) {
			try {
				return queryParamsToFilters(
					new URLSearchParams(location.search),
					filterDefinitions,
					filterOptions
				);
			} catch (error) {
				Logger.error('Failed to parse query parameters for filters during initialization:', error);
				return {};
			}
		}

		return {};
	});

	const lastInitialized = useRef<{ search: string; readyOptionKeys: string }>({
		search: location.search,
		readyOptionKeys: getReadyOptionKeys(filterOptions, location.search)
	});

	useEffect(() => {
		const hasOptions = Object.keys(filterOptions).length > 0;
		const hasDefinitions = filterDefinitions.length > 0;
		const hasSearch = Boolean(location.search);
		const readyOptionKeys = getReadyOptionKeys(filterOptions, location.search);

		// Should initialize if:
		// 1. We have both definitions and options
		// 2. URL has query params
		// 3. Either the URL changed OR a filter key in the URL gained its options since the
		//    last parse. Option lists such as severities/types arrive from the API after the
		//    first render; `queryParamsToFilters` skips a key whose options are still empty,
		//    so the URL has to be parsed again once they are available.
		//    Known limit: if the user applies a filter while a URL key is still waiting for
		//    its options, the URL is rewritten from the applied filters and that key is lost.
		const shouldInitialize =
			hasDefinitions &&
			hasOptions &&
			hasSearch &&
			(location.search !== lastInitialized.current.search ||
				readyOptionKeys !== lastInitialized.current.readyOptionKeys);

		if (shouldInitialize) {
			try {
				const initialFilters = queryParamsToFilters(
					new URLSearchParams(location.search),
					filterDefinitions,
					filterOptions
				);

				setFilters(initialFilters);
			} catch (error) {
				Logger.error('Failed to parse query parameters for filters:', error);
				// Reset to empty filters on error to prevent breaking the page
				setFilters({});
			}
			lastInitialized.current = {
				search: location.search,
				readyOptionKeys
			};
		}
	}, [filterDefinitions, filterOptions, location.search]);

	const handleFilterChange = useCallback(
		(newFilters: IFilterOptions): void => {
			setFilters(newFilters);
			updateUrlWithFilters(history, location, newFilters, filterDefinitions);

			lastInitialized.current = {
				search: history.location.search,
				readyOptionKeys: getReadyOptionKeys(filterOptions, history.location.search)
			};
		},
		[history, location, filterDefinitions, filterOptions]
	);

	return {
		filters,
		handleFilterChange
	};
};
